(() => {
  const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
  const POPOVER_GAP = 8;
  const POPOVER_ANIM_MS = 340;
  const POPOVER_HOVER_CLOSE_DELAY = 150;
  const POPOVER_PLACEMENTS = ['top', 'bottom', 'left', 'right'];
  const POPOVER_VERTICAL = ['top', 'bottom'];
  const POPOVER_HORIZONTAL = ['left', 'right'];
  const POPOVER_PLACEMENT_CLASSES = POPOVER_PLACEMENTS.map(
    placement => `is-popover-${placement}`
  );

  const init = root => {
    if (root.dataset.livingStateReady === 'true') return;
    root.dataset.livingStateReady = 'true';

    const abortController = new AbortController();
    const { signal } = abortController;
    root.livingStateAbortController = abortController;

    const tabs = [...root.querySelectorAll('[data-living-tab]')];
    const panels = [...root.querySelectorAll('[data-living-panel]')];
    const descItems = [...root.querySelectorAll('[data-living-desc-item]')];
    const hotspots = [...root.querySelectorAll('[data-living-hotspot]')];
    const tabIndicator = root.querySelector('[data-living-tab-indicator]');
    const tabsWrap = root.querySelector('[data-living-tabs-wrap]');
    const tabsViewport = root.querySelector('[data-living-tabs-viewport]');
    const tabsContainer = root.querySelector('[data-living-tabs]');
    const tabsEdgeStart = root.querySelector('[data-living-tabs-edge-start]');
    const tabsEdgeEnd = root.querySelector('[data-living-tabs-edge-end]');
    const tabsPrev = root.querySelector('[data-living-tabs-prev]');
    const tabsNext = root.querySelector('[data-living-tabs-next]');

    const autoRotateEnabled =
      root.dataset.autoRotateTabs === 'true' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoRotateInterval = Math.max(1, parseInt(root.dataset.autoRotateInterval, 10) || 5) * 1000;

    let activeTabIndex = tabs.findIndex(tab => tab.classList.contains('is-active'));
    if (activeTabIndex < 0) activeTabIndex = 0;

    let autoRotateTimer = null;
    let hoverLeaveTimer = null;
    const popoverExitTimers = new WeakMap();

    const isHoverCapable = () => window.matchMedia(HOVER_QUERY).matches;
    const prefersReducedMotion = () =>
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const finishCloseHotspot = hotspot => {
      const timer = popoverExitTimers.get(hotspot);
      if (timer) {
        clearTimeout(timer);
        popoverExitTimers.delete(hotspot);
      }

      hotspot.classList.remove(
        'is-active',
        'is-popover-ready',
        'is-popover-visible',
        'is-popover-leaving'
      );
      clearPopoverPlacement(hotspot);
      hotspot.querySelector('[data-living-popover]')?.style.removeProperty('transform-origin');
      hotspot.querySelector('[data-living-hotspot-button]')?.setAttribute('aria-expanded', 'false');
    };

    const closeHotspot = (hotspot, { immediate = false } = {}) => {
      if (!hotspot.classList.contains('is-active')) return;

      if (immediate || prefersReducedMotion()) {
        finishCloseHotspot(hotspot);
        return;
      }

      hotspot.classList.remove('is-popover-ready', 'is-popover-visible');
      hotspot.classList.add('is-popover-leaving');

      const existingTimer = popoverExitTimers.get(hotspot);
      if (existingTimer) clearTimeout(existingTimer);

      const timer = setTimeout(() => finishCloseHotspot(hotspot), POPOVER_ANIM_MS + 50);
      popoverExitTimers.set(hotspot, timer);
    };

    const closeHotspots = (scope = root, { immediate = false } = {}) => {
      clearTimeout(hoverLeaveTimer);
      scope.querySelectorAll('[data-living-hotspot].is-active').forEach(hotspot => {
        closeHotspot(hotspot, { immediate });
      });
    };

    const clearPopoverPlacement = hotspot => {
      hotspot.classList.remove(...POPOVER_PLACEMENT_CLASSES);
    };

    const getPopoverRectForPlacement = (hotspotRect, popoverRect, placement) => {
      const hotspotCenterX = hotspotRect.left + hotspotRect.width / 2;
      const hotspotCenterY = hotspotRect.top + hotspotRect.height / 2;

      let left;
      let top;

      switch (placement) {
        case 'top':
          left = hotspotCenterX - popoverRect.width / 2;
          top = hotspotRect.top - POPOVER_GAP - popoverRect.height;
          break;
        case 'bottom':
          left = hotspotCenterX - popoverRect.width / 2;
          top = hotspotRect.bottom + POPOVER_GAP;
          break;
        case 'left':
          left = hotspotRect.left - POPOVER_GAP - popoverRect.width;
          top = hotspotCenterY - popoverRect.height / 2;
          break;
        default:
          left = hotspotRect.right + POPOVER_GAP;
          top = hotspotCenterY - popoverRect.height / 2;
          break;
      }

      return {
        left,
        top,
        right: left + popoverRect.width,
        bottom: top + popoverRect.height,
      };
    };

    const popoverFitsMedia = (popoverBounds, mediaRect) =>
      popoverBounds.left >= mediaRect.left &&
      popoverBounds.top >= mediaRect.top &&
      popoverBounds.right <= mediaRect.right &&
      popoverBounds.bottom <= mediaRect.bottom;

    const getPopoverSpace = (hotspotRect, mediaRect) => ({
      top: hotspotRect.top - mediaRect.top,
      bottom: mediaRect.bottom - hotspotRect.bottom,
      left: hotspotRect.left - mediaRect.left,
      right: mediaRect.right - hotspotRect.right,
    });

    const pickPlacementBySpace = (candidates, space) => {
      if (candidates.length === 1) return candidates[0];

      const hasTop = candidates.includes('top');
      const hasBottom = candidates.includes('bottom');
      if (hasTop && hasBottom) {
        return space.top >= space.bottom ? 'top' : 'bottom';
      }

      const hasLeft = candidates.includes('left');
      const hasRight = candidates.includes('right');
      if (hasLeft && hasRight) {
        return space.left >= space.right ? 'left' : 'right';
      }

      return candidates[0];
    };

    const resolvePopoverPlacement = (hotspotRect, popoverRect, mediaRect) => {
      const space = getPopoverSpace(hotspotRect, mediaRect);
      const fitting = POPOVER_PLACEMENTS.filter(placement =>
        popoverFitsMedia(getPopoverRectForPlacement(hotspotRect, popoverRect, placement), mediaRect)
      );

      const fittingVertical = fitting.filter(placement => POPOVER_VERTICAL.includes(placement));
      if (fittingVertical.length > 0) {
        return pickPlacementBySpace(fittingVertical, space);
      }

      const fittingHorizontal = fitting.filter(placement => POPOVER_HORIZONTAL.includes(placement));
      if (fittingHorizontal.length > 0) {
        return pickPlacementBySpace(fittingHorizontal, space);
      }

      const maxVerticalSpace = Math.max(space.top, space.bottom);
      const maxHorizontalSpace = Math.max(space.left, space.right);

      if (maxVerticalSpace >= maxHorizontalSpace) {
        return space.top >= space.bottom ? 'top' : 'bottom';
      }

      return space.left >= space.right ? 'left' : 'right';
    };

    const setPopoverTransformOrigin = (hotspot, popover) => {
      const hotspotRect = hotspot.getBoundingClientRect();
      const popoverRect = popover.getBoundingClientRect();
      const originX = hotspotRect.left + hotspotRect.width / 2 - popoverRect.left;
      const originY = hotspotRect.top + hotspotRect.height / 2 - popoverRect.top;
      popover.style.transformOrigin = `${originX}px ${originY}px`;
    };

    const placePopover = hotspot => {
      const media = hotspot.closest('.living-state__media');
      const popover = hotspot.querySelector('[data-living-popover]');
      if (!media || !popover) return;

      clearPopoverPlacement(hotspot);

      const mediaRect = media.getBoundingClientRect();
      const hotspotRect = hotspot.getBoundingClientRect();
      const popoverRect = popover.getBoundingClientRect();
      if (!popoverRect.width || !popoverRect.height) return;

      const placement = resolvePopoverPlacement(hotspotRect, popoverRect, mediaRect);
      if (placement !== 'right') {
        hotspot.classList.add(`is-popover-${placement}`);
      }

      setPopoverTransformOrigin(hotspot, popover);
    };

    const restoreHotspotVisible = hotspot => {
      const exitTimer = popoverExitTimers.get(hotspot);
      if (exitTimer) {
        clearTimeout(exitTimer);
        popoverExitTimers.delete(hotspot);
      }

      hotspot.classList.remove('is-popover-leaving');
      hotspot.classList.add('is-popover-ready', 'is-popover-visible');
      hotspot.querySelector('[data-living-hotspot-button]')?.setAttribute('aria-expanded', 'true');
    };

    const cancelHotspotClose = hotspot => {
      clearTimeout(hoverLeaveTimer);
      if (!hotspot?.classList.contains('is-active')) return;

      if (
        hotspot.classList.contains('is-popover-leaving') ||
        popoverExitTimers.has(hotspot)
      ) {
        restoreHotspotVisible(hotspot);
      }
    };

    const openHotspot = hotspot => {
      const panel = hotspot.closest('[data-living-panel]');
      if (!panel) return;

      if (hotspot.classList.contains('is-active')) {
        cancelHotspotClose(hotspot);
        return;
      }

      panel.querySelectorAll('[data-living-hotspot].is-active').forEach(other => {
        if (other !== hotspot) closeHotspot(other);
      });

      hotspot.classList.remove('is-popover-leaving', 'is-popover-ready', 'is-popover-visible');
      hotspot.classList.add('is-active');
      hotspot.querySelector('[data-living-hotspot-button]')?.setAttribute('aria-expanded', 'true');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          placePopover(hotspot);
          hotspot.classList.add('is-popover-ready');

          if (prefersReducedMotion()) {
            hotspot.classList.add('is-popover-visible');
            return;
          }

          requestAnimationFrame(() => {
            hotspot.classList.add('is-popover-visible');
          });
        });
      });
    };

    const activateHotspot = hotspot => {
      if (hotspot.classList.contains('is-active')) {
        closeHotspot(hotspot);
        return;
      }
      openHotspot(hotspot);
    };

    const scheduleHotspotClose = hotspot => {
      clearTimeout(hoverLeaveTimer);
      hoverLeaveTimer = setTimeout(() => {
        if (!hotspot.classList.contains('is-active')) return;
        closeHotspot(hotspot);
      }, POPOVER_HOVER_CLOSE_DELAY);
    };

    const isHotspotHoverTarget = (hotspot, target) => {
      if (!target || !hotspot.contains(target)) return false;
      return Boolean(
        target.closest('[data-living-hotspot-button]') ||
          target.closest('[data-living-popover]')
      );
    };

    const isTabsScrollable = () => tabsWrap?.classList.contains('is-scrollable');

    const getTabsNaturalWidth = () => {
      if (!tabsContainer || tabs.length === 0) return 0;

      tabsContainer.classList.add('is-measuring');
      const styles = getComputedStyle(tabsContainer);
      const padding =
        (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const naturalWidth =
        padding +
        tabs.reduce((sum, tab, index) => sum + tab.offsetWidth + (index > 0 ? gap : 0), 0);
      tabsContainer.classList.remove('is-measuring');
      return naturalWidth;
    };

    const updateTabsOverflow = () => {
      if (!tabsWrap || !tabsContainer) return;

      tabsWrap.classList.remove('is-scrollable');
      const availableWidth = tabsWrap.clientWidth;
      const naturalWidth = getTabsNaturalWidth();
      const shouldScroll = naturalWidth > availableWidth + 1;

      tabsWrap.classList.toggle('is-scrollable', shouldScroll);
      if (!shouldScroll && tabsViewport) {
        tabsViewport.scrollLeft = 0;
      }
      updateTabsScrollEdges();
    };

    const updateTabsScrollEdges = () => {
      if (!isTabsScrollable() || !tabsViewport) {
        tabsEdgeStart?.setAttribute('hidden', '');
        tabsEdgeEnd?.setAttribute('hidden', '');
        return;
      }

      const maxScroll = tabsViewport.scrollWidth - tabsViewport.clientWidth;
      const canScrollStart = tabsViewport.scrollLeft > 1;
      const canScrollEnd = tabsViewport.scrollLeft < maxScroll - 1;

      if (canScrollStart) {
        tabsEdgeStart?.removeAttribute('hidden');
      } else {
        tabsEdgeStart?.setAttribute('hidden', '');
      }

      if (canScrollEnd) {
        tabsEdgeEnd?.removeAttribute('hidden');
      } else {
        tabsEdgeEnd?.setAttribute('hidden', '');
      }
    };

    const scrollTabsBy = delta => {
      if (!tabsViewport) return;
      tabsViewport.scrollBy({ left: delta, behavior: 'smooth' });
    };

    const scrollTabIntoView = (index, { behavior = 'smooth' } = {}) => {
      const tab = tabs[index];
      if (!tab || !tabsViewport || !isTabsScrollable()) return;

      const tabCenter = tab.offsetLeft + tab.offsetWidth / 2;
      const viewportWidth = tabsViewport.clientWidth;
      const maxScroll = Math.max(0, tabsViewport.scrollWidth - viewportWidth);
      const targetScroll = Math.min(maxScroll, Math.max(0, tabCenter - viewportWidth / 2));

      tabsViewport.scrollTo({ left: targetScroll, behavior });

      const syncAfterScroll = () => {
        setTabIndicatorInstant(index);
        updateTabsScrollEdges();
      };

      if (behavior === 'smooth') {
        window.setTimeout(syncAfterScroll, 420);
      } else {
        syncAfterScroll();
      }
    };

    const INDICATOR_DURATION = 520;
    const INDICATOR_MIN_WIDTH_RATIO = 0.5;

    const getTabMetrics = tab => ({
      x: tab.offsetLeft,
      y: tab.offsetTop,
      w: tab.offsetWidth,
      h: tab.offsetHeight,
    });

    const setTabIndicatorInstant = index => {
      const tab = tabs[index];
      if (!tab || !tabIndicator) return;

      tabIndicator.getAnimations().forEach(animation => animation.cancel());
      const metrics = getTabMetrics(tab);
      tabIndicator.style.width = `${metrics.w}px`;
      tabIndicator.style.height = `${metrics.h}px`;
      tabIndicator.style.transform = `translate(${metrics.x}px, ${metrics.y}px)`;
    };

    const animateTabIndicator = (fromIndex, toIndex) => {
      const fromTab = tabs[fromIndex];
      const toTab = tabs[toIndex];
      if (!fromTab || !toTab || !tabIndicator) return;

      if (
        fromIndex === toIndex ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        setTabIndicatorInstant(toIndex);
        return;
      }

      const from = getTabMetrics(fromTab);
      const to = getTabMetrics(toTab);
      const fromCenter = from.x + from.w / 2;
      const toCenter = to.x + to.w / 2;
      const midCenter = (fromCenter + toCenter) / 2;
      const minWidth = Math.max(10, Math.min(from.w, to.w) * INDICATOR_MIN_WIDTH_RATIO);
      const midY = from.y + (to.y - from.y) * 0.5;
      const midHeight = (from.h + to.h) / 2;

      tabIndicator.getAnimations().forEach(animation => animation.cancel());

      const animation = tabIndicator.animate(
        [
          {
            transform: `translate(${from.x}px, ${from.y}px)`,
            width: `${from.w}px`,
            height: `${from.h}px`,
          },
          {
            transform: `translate(${midCenter - minWidth / 2}px, ${midY}px)`,
            width: `${minWidth}px`,
            height: `${midHeight}px`,
            offset: 0.5,
          },
          {
            transform: `translate(${to.x}px, ${to.y}px)`,
            width: `${to.w}px`,
            height: `${to.h}px`,
          },
        ],
        {
          duration: INDICATOR_DURATION,
          easing: 'cubic-bezier(0.45, 0.05, 0.2, 1)',
          fill: 'forwards',
        }
      );

      animation.onfinish = () => setTabIndicatorInstant(toIndex);
      animation.oncancel = () => setTabIndicatorInstant(toIndex);
    };

    const stopAutoRotate = () => {
      if (!autoRotateTimer) return;
      clearInterval(autoRotateTimer);
      autoRotateTimer = null;
    };

    const startAutoRotate = () => {
      stopAutoRotate();
      if (!autoRotateEnabled || tabs.length <= 1) return;

      autoRotateTimer = setInterval(() => {
        const nextIndex = (activeTabIndex + 1) % tabs.length;
        activate(nextIndex);
      }, autoRotateInterval);
    };

    const restartAutoRotate = () => {
      if (!autoRotateEnabled) return;
      startAutoRotate();
    };

    const activate = (index, { userInitiated = false } = {}) => {
      const previousTabIndex = activeTabIndex;
      activeTabIndex = index;

      tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel, i) => {
        const selected = i === index;
        panel.classList.toggle('is-active', selected);
        panel.setAttribute('aria-hidden', String(!selected));
        if (selected) {
          panel.removeAttribute('inert');
        } else {
          panel.setAttribute('inert', '');
        }
      });
      descItems.forEach((item, i) => {
        const selected = i === index;
        item.classList.toggle('is-active', selected);
        item.setAttribute('aria-hidden', String(!selected));
      });
      closeHotspots(root, { immediate: true });

      if (previousTabIndex !== index) {
        animateTabIndicator(previousTabIndex, index);
      } else {
        setTabIndicatorInstant(index);
      }

      scrollTabIntoView(index);

      if (userInitiated) {
        restartAutoRotate();
      }
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener(
        'click',
        () => activate(index, { userInitiated: true }),
        { signal }
      );
      tab.addEventListener(
        'keydown',
        event => {
          const keys = { ArrowRight: 1, ArrowLeft: -1, Home: -index, End: tabs.length - 1 - index };
          if (!(event.key in keys)) return;
          event.preventDefault();
          const next = (index + keys[event.key] + tabs.length) % tabs.length;
          tabs[next].focus();
          activate(next, { userInitiated: true });
        },
        { signal }
      );
    });

    hotspots.forEach(hotspot => {
      const button = hotspot.querySelector('[data-living-hotspot-button]');
      const popover = hotspot.querySelector('[data-living-popover]');

      hotspot.addEventListener(
        'mouseenter',
        () => {
          if (!isHoverCapable()) return;
          cancelHotspotClose(hotspot);
          openHotspot(hotspot);
        },
        { signal }
      );
      hotspot.addEventListener(
        'mouseleave',
        event => {
          if (!isHoverCapable()) return;
          if (isHotspotHoverTarget(hotspot, event.relatedTarget)) return;
          scheduleHotspotClose(hotspot);
        },
        { signal }
      );
      popover?.addEventListener(
        'mouseenter',
        () => {
          if (!isHoverCapable()) return;
          cancelHotspotClose(hotspot);
        },
        { signal }
      );
      popover?.addEventListener(
        'mouseleave',
        event => {
          if (!isHoverCapable()) return;
          if (isHotspotHoverTarget(hotspot, event.relatedTarget)) return;
          scheduleHotspotClose(hotspot);
        },
        { signal }
      );

      button?.addEventListener(
        'click',
        event => {
          if (isHoverCapable()) return;
          event.stopPropagation();
          activateHotspot(hotspot);
        },
        { signal }
      );

      button?.addEventListener(
        'focus',
        () => {
          if (!isHoverCapable()) return;
          openHotspot(hotspot);
        },
        { signal }
      );
      button?.addEventListener(
        'blur',
        event => {
          if (!isHoverCapable()) return;
          if (hotspot.contains(event.relatedTarget)) return;
          closeHotspot(hotspot);
        },
        { signal }
      );
    });

    document.addEventListener(
      'click',
      event => {
        if (isHoverCapable()) return;
        if (event.target.closest('[data-living-hotspot]')) return;
        closeHotspots(root);
      },
      { signal }
    );

    const closeHotspotsOnScroll = () => {
      if (isHoverCapable()) return;
      closeHotspots(root, { immediate: true });
    };

    window.addEventListener('scroll', closeHotspotsOnScroll, { passive: true, capture: true, signal });

    root.addEventListener(
      'keydown',
      event => {
        if (event.key !== 'Escape') return;
        closeHotspots(root);
      },
      { signal }
    );

    tabsPrev?.addEventListener(
      'click',
      () => scrollTabsBy(-(tabsViewport?.clientWidth || 240) * 0.75),
      { signal }
    );

    tabsNext?.addEventListener(
      'click',
      () => scrollTabsBy((tabsViewport?.clientWidth || 240) * 0.75),
      { signal }
    );

    tabsViewport?.addEventListener(
      'scroll',
      () => {
        setTabIndicatorInstant(activeTabIndex);
        updateTabsScrollEdges();
      },
      { passive: true, signal }
    );

    if (tabsWrap && 'ResizeObserver' in window) {
      const tabsResizeObserver = new ResizeObserver(() => updateTabsOverflow());
      tabsResizeObserver.observe(tabsWrap);
      abortController.signal.addEventListener('abort', () => tabsResizeObserver.disconnect());
    }

    document.fonts?.ready.then(() => {
      updateTabsOverflow();
      setTabIndicatorInstant(activeTabIndex);
    });

    window.addEventListener(
      'resize',
      () => {
        updateTabsOverflow();
        setTabIndicatorInstant(activeTabIndex);
        const active = root.querySelector('[data-living-hotspot].is-popover-visible');
        if (active) placePopover(active);
      },
      { signal }
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          stopAutoRotate();
          return;
        }
        startAutoRotate();
      },
      { signal }
    );

    updateTabsOverflow();
    setTabIndicatorInstant(activeTabIndex);
    scrollTabIntoView(activeTabIndex, { behavior: 'instant' });
    requestAnimationFrame(() => {
      updateTabsOverflow();
      setTabIndicatorInstant(activeTabIndex);
      scrollTabIntoView(activeTabIndex, { behavior: 'instant' });
    });
    panels.forEach((panel, i) => {
      if (i === activeTabIndex) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    });
    startAutoRotate();
  };

  const initAll = scope => scope.querySelectorAll('[data-living-state]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelector('[data-living-state]')?.livingStateAbortController?.abort();
  });
})();
